import type { Localized } from '@porotutu/http/vocabulary'
import { type Locale, localeOf, type Translator, translatorFor } from '@porotutu/i18n'
import { type Permit, permit } from '@porotutu/react/permit'
import {
  type UseMutationOptions,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { notifyBroken, notifyDone } from '../components/notify'
import { guestLocale, rememberLocale } from './locale'

export type Viewer = {
  locale: string
  name: string
  email: string
  permissions: string[]
  organization_name: string | null
}

export type Session<V extends Viewer> = {
  viewer: V | null
  locale: Locale
  t: Translator
  permissions: Permit
  organization: string | null
  setLocale: (locale: Locale) => void
}

// biome-ignore lint/suspicious/noExplicitAny: the brand queryOptions adds cannot be named here
export type ProfileQuery = any

export type SessionConfig<V extends Viewer> = {
  profileQuery: ProfileQuery
  changeLocaleMutation: (
    queryClient: ReturnType<typeof useQueryClient>,
    // biome-ignore lint/suspicious/noExplicitAny: each surface's mutation names its own error and context types
  ) => UseMutationOptions<V, any, { locale: Locale }, any>
}

const SessionContext = createContext<Session<Viewer> | null>(null)

export function SessionProvider<V extends Viewer>({
  config,
  children,
}: {
  config: SessionConfig<V>
  children: ReactNode
}) {
  const queryClient = useQueryClient()
  const { data: profile } = useSuspenseQuery<V | null>(config.profileQuery)
  const [guest, setGuest] = useState(guestLocale)
  const { mutate: saveLocale } = useMutation(config.changeLocaleMutation(queryClient))

  const setLocale = useCallback(
    (locale: Locale) => {
      rememberLocale(locale)
      setGuest(locale)

      if (profile == null) return

      const spoken = translatorFor(locale)

      saveLocale(
        { locale },
        {
          onSuccess: () => notifyDone(spoken('language.saved')),
          onError: () => notifyBroken(spoken('language.not_saved')),
        },
      )
    },
    [profile, saveLocale],
  )

  const session = useMemo<Session<V>>(() => {
    const locale = profile == null ? guest : localeOf(profile.locale)

    return {
      viewer: profile ?? null,
      locale,
      t: translatorFor(locale),
      permissions: permit(profile?.permissions ?? []),
      organization: profile?.organization_name ?? null,
      setLocale,
    }
  }, [profile, guest, setLocale])

  useEffect(() => {
    document.documentElement.lang = session.locale
  }, [session.locale])

  return (
    <SessionContext.Provider value={session as Session<Viewer>}>{children}</SessionContext.Provider>
  )
}

export function useSession<V extends Viewer = Viewer>(): Session<V> {
  const session = useContext(SessionContext)

  if (session === null) throw new Error('useSession outside SessionProvider')

  return session as Session<V>
}

export const useMaybeSession = (): Session<Viewer> | null => useContext(SessionContext)

export const useT = (): Translator => useMaybeSession()?.t ?? translatorFor(guestLocale())

export type Spelled = (name: Localized) => string

export function useName(): Spelled {
  const locale = useMaybeSession()?.locale ?? guestLocale()

  return useCallback((name: Localized) => name[locale] || name.pl || name.en, [locale])
}

export function useViewer<V extends Viewer>(): V {
  const { viewer } = useSession<V>()

  if (viewer === null) throw new Error('useViewer outside the signed-in layout')

  return viewer
}

export function sessionFor<V extends Viewer>(config: SessionConfig<V>) {
  return {
    config,
    useSession: () => useSession<V>(),
    useViewer: () => useViewer<V>(),
  }
}
